import { useState, useEffect } from "react";
import { chatData } from "@/data/chatData";

const useChatBot = () => {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [userInput, setUserInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentTopic, setCurrentTopic] = useState("");

  // Initialize chat with welcome message
  useEffect(() => {
    setMessages([
      {
        sender: "ai",
        content: "Hi there! I'm Alex AI, your guide to learning more about Alex. What would you like to know about?"
      }
    ]);
  }, []);

  // Function to add a user message
  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { sender: "user", content: text }]);
    setShowSuggestions(false);
  };

  // Function to add AI response after typing effect
  const addAIResponse = (content, topic = "") => {
    setIsTyping(true);
    setCurrentTopic(topic);
    
    // Simulate typing delay based on content length
    const typingDelay = Math.min(1500, 500 + content.length / 5);
    
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, { sender: "ai", content }]);
      setShowSuggestions(true);
    }, typingDelay);
  };

  // Handle sending a message
  const handleSendMessage = () => {
    const trimmedInput = userInput.trim();
    if (!trimmedInput) return;
    
    addUserMessage(trimmedInput);
    setUserInput("");
    
    // Process user input
    processUserInput(trimmedInput);
  };

  // Handle clicking a section option
  const handleOptionClick = (section) => {
    const sectionLabels = {
      about: "Tell me about Alex's background",
      investments: "Show me Alex's investments",
      projects: "Tell me about Alex's builder journey",
      ramblings: "Show me Alex's ramblings",
      philosophy: "Tell me about Alex's life philosophy"
    };
    
    const message = sectionLabels[section] || `Tell me about ${section}`;
    addUserMessage(message);
    
    // Get section content from chatData
    const sectionContent = chatData[section];
    if (sectionContent) {
      addAIResponse(sectionContent, section);
    } else {
      addAIResponse("I don't have information about that topic yet. Let me know if you'd like to know about Alex's background, investments, projects, or ramblings.");
    }
  };

  // Process user input to determine response
  const processUserInput = (input) => {
    const lowerInput = input.toLowerCase();
    
    // Check for keywords in user input
    if (lowerInput.includes("about") || lowerInput.includes("background") || lowerInput.includes("who")) {
      addAIResponse(chatData.about, "about");
    } else if (lowerInput.includes("invest")) {
      addAIResponse(chatData.investments, "investments");
    } else if (lowerInput.includes("project") || lowerInput.includes("build") || lowerInput.includes("journey")) {
      addAIResponse(chatData.projects, "projects");
    } else if (lowerInput.includes("rambling") || lowerInput.includes("blog") || lowerInput.includes("thought")) {
      addAIResponse(chatData.ramblings, "ramblings");
    } else if (lowerInput.includes("philosophy") || lowerInput.includes("life")) {
      addAIResponse(chatData.philosophy, "philosophy");
    } else {
      // Default response for unrecognized inputs
      addAIResponse("I can tell you about Alex's background, investments, projects, or share some of his ramblings. What would you like to know?");
    }
  };

  // Reset chat to initial state
  const resetChat = () => {
    setMessages([
      {
        sender: "ai",
        content: "Hi there! I'm Alex AI, your guide to learning more about Alex. What would you like to know about?"
      }
    ]);
  };

  // Get follow-up suggestions based on current topic
  const getFollowUpSuggestions = () => {
    switch(currentTopic) {
      case 'about':
        return [
          { id: 'philosophy', label: 'Tell me about Alex\'s life philosophy' },
          { id: 'projects', label: 'What projects has Alex worked on?' },
          { id: 'investments', label: 'What has Alex invested in?' }
        ];
      case 'investments':
        return [
          { id: 'projects', label: 'What projects has Alex built?' },
          { id: 'about', label: 'Tell me more about Alex\'s background' },
          { id: 'ramblings', label: 'Any thoughts on the state of tech?' }
        ];
      case 'projects':
        return [
          { id: 'investments', label: 'What has Alex invested in?' },
          { id: 'ramblings', label: 'Alex\'s thoughts on building?' },
          { id: 'about', label: 'More about Alex\'s experience' }
        ];
      case 'ramblings':
        return [
          { id: 'philosophy', label: 'Alex\'s life philosophy' },
          { id: 'investments', label: 'Alex\'s investment approach' },
          { id: 'projects', label: 'Tell me about some of Alex\'s projects' }
        ];
      case 'philosophy':
        return [
          { id: 'about', label: 'More about Alex\'s background' },
          { id: 'ramblings', label: 'Alex\'s thoughts on wealth' },
          { id: 'projects', label: 'Alex\'s approach to building' },
          
        ];
      default:
        return [
          { id: 'about', label: 'Tell me about Alex\'s background' },
          { id: 'investments', label: 'What has Alex invested in?' },
          { id: 'projects', label: 'Alex\'s builder journey' },
          { id: 'ramblings', label: 'Alex\'s thoughts and ideas' }
        ];
    }
  };

  return {
    messages,
    isTyping,
    userInput,
    setUserInput,
    handleSendMessage,
    handleOptionClick,
    resetChat,
    showSuggestions,
    getFollowUpSuggestions
  };
};

export default useChatBot;
